import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import type { SupportedLocale, UserPublic } from '@connexio/shared';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@connexio/shared/constants';

const LOCALE_STORAGE_KEY = 'freelancecrm-locale';

export function useLocale() {
  const { i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const locale: SupportedLocale =
    SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
      ? (i18n.language as SupportedLocale)
      : DEFAULT_LOCALE;

  const changeLanguage = useCallback(
    async (newLocale: SupportedLocale) => {
      await i18n.changeLanguage(newLocale);
      // Anonymous visitors have no profile to store this on, so the choice would
      // otherwise be lost on reload.
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
      } catch {
        // Private browsing / storage disabled — language is already applied in memory.
      }

      if (user) {
        try {
          // Loaded on demand: the marketing pages use this hook and must not ship axios (CX-397).
          const { apiClient } = await import('../lib/apiClient');
          const { data } = await apiClient.put<{ success: boolean; data: UserPublic }>('/settings/profile', { locale: newLocale });
          if (data.success && data.data) {
            setUser(data.data);
          }
        } catch {
          // Silently fail — the UI language is already changed
        }
      }
    },
    [i18n, user, setUser],
  );

  return { locale, changeLanguage };
}
